import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  IndianRupee, TrendingUp, Users, ArrowUpRight, RefreshCcw,
  Loader2, BarChart3, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCommissionPage({ admin }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('dharmbook_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/payments/commission-stats`, { headers });
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20 text-slate-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>Failed to load commission data</p>
        <Button onClick={fetchStats} variant="outline" className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-commission-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Commission & Revenue</h1>
          <p className="text-slate-500 text-sm mt-1">Platform earnings from pandit bookings ({stats.commission_rate} commission)</p>
        </div>
        <Button onClick={fetchStats} variant="outline" size="sm">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="commission-stats-cards">
        <StatCard
          label="Total Revenue"
          value={`₹${stats.total_revenue?.toLocaleString('en-IN') || '0'}`}
          subtitle="Gross booking value"
          icon={IndianRupee}
          color="bg-green-50 text-green-600"
        />
        <StatCard
          label="Platform Commission"
          value={`₹${stats.total_commission?.toLocaleString('en-IN') || '0'}`}
          subtitle={`${stats.commission_rate} of total revenue`}
          icon={TrendingUp}
          color="bg-orange-50 text-orange-600"
        />
        <StatCard
          label="Pandit Payouts"
          value={`₹${stats.total_pandit_payouts?.toLocaleString('en-IN') || '0'}`}
          subtitle="Due to pandits (80%)"
          icon={Users}
          color="bg-blue-50 text-blue-600"
        />
        <StatCard
          label="Refunded"
          value={`₹${stats.total_refunded?.toLocaleString('en-IN') || '0'}`}
          subtitle={`${stats.total_refund_count || 0} refund(s)`}
          icon={ArrowDownRight}
          color="bg-red-50 text-red-500"
        />
      </div>

      {/* Summary Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Revenue Breakdown</h3>
          <Badge variant="outline">{stats.total_transactions} transactions</Badge>
        </div>
        <div className="h-4 bg-slate-100 rounded-full overflow-hidden flex">
          {stats.total_revenue > 0 && (
            <>
              <div
                className="bg-orange-500 h-full transition-all"
                style={{ width: `${((stats.total_commission / stats.total_revenue) * 100).toFixed(1)}%` }}
                title={`Commission: ₹${stats.total_commission}`}
              />
              <div
                className="bg-blue-400 h-full transition-all"
                style={{ width: `${((stats.total_pandit_payouts / stats.total_revenue) * 100).toFixed(1)}%` }}
                title={`Pandit Payouts: ₹${stats.total_pandit_payouts}`}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" /> Commission ({stats.commission_rate})</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" /> Pandit Payouts (80%)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Earning Pandits */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">Top Earning Pandits</h3>
          </div>
          {stats.top_pandits?.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {stats.top_pandits.map((p, i) => (
                <div key={p.pandit_id} className="px-4 py-3 flex items-center gap-3" data-testid={`top-pandit-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.name || 'Unknown'}</p>
                    <p className="text-xs text-slate-400">{p.city} &middot; {p.bookings} bookings</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-green-600">₹{p.total_earned?.toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-slate-400">Commission: ₹{p.commission_generated?.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">No earnings data yet</div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">Recent Transactions</h3>
          </div>
          {stats.recent_transactions?.length > 0 ? (
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {stats.recent_transactions.map((tx, i) => (
                <div key={tx.payment_id} className="px-4 py-3" data-testid={`transaction-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-slate-800">{tx.pooja_type || 'Pooja'}</p>
                    <p className="text-sm font-bold text-slate-800">₹{tx.amount?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{tx.devotee_name} → {tx.pandit_name}</span>
                    <span className="text-orange-600 font-medium">Commission: ₹{tx.commission}</span>
                  </div>
                  {tx.paid_at && (
                    <p className="text-[10px] text-slate-300 mt-0.5">
                      {new Date(tx.paid_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">No transactions yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      <p className="text-[10px] text-slate-400">{subtitle}</p>
    </div>
  );
}
